(() => {
  'use strict';

  const instances = new WeakMap();

  class WelcomeVisualController {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.saveData = Boolean(navigator.connection?.saveData);
      this.videos = Array.from(root.querySelectorAll('video'));
      this.intro = root.querySelector('[data-ldw-intro]');
    }

    connect() {
      if (this.root.dataset.ldWelcomeVisualReady === 'true') return;
      this.root.dataset.ldWelcomeVisualReady = 'true';
      this.root.classList.add('is-js', 'is-scene-ready');
      this.intro?.setAttribute('hidden', '');
      this.intro?.setAttribute('aria-hidden', 'true');
      document.body.classList.add('ldw-page-active', 'ldw-gate-active');
      document.body.classList.remove('ldw-hide-footer');

      this.root.querySelectorAll('[data-ldw-path]').forEach((path) => {
        const mode = path.dataset.ldwPath;
        const activate = () => {
          if (this.root.dataset.exitState === 'EXIT_TO_HOME') return;
          this.root.dataset.activePath = mode;
          this.syncVideos();
        };
        const reset = () => {
          if (this.root.dataset.exitState === 'EXIT_TO_HOME') return;
          if (!this.root.contains(document.activeElement)) this.root.removeAttribute('data-active-path');
          this.syncVideos();
        };
        path.addEventListener('pointerenter', activate, { signal: this.signal });
        path.addEventListener('focus', activate, { signal: this.signal });
        path.addEventListener('pointerleave', reset, { signal: this.signal });
        path.addEventListener('blur', reset, { signal: this.signal });
      });

      document.addEventListener('dv2_welcome_choice', (event) => {
        const sectionId = event.detail?.sectionId;
        if (sectionId && sectionId !== this.root.dataset.sectionId) return;
        if (event.detail?.choice === 'home') {
          this.root.dataset.exitState = 'EXIT_TO_HOME';
          this.root.dataset.activePath = 'home';
          this.root.classList.add('is-exit-to-home');
        }
        this.pauseAll();
      }, { signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAll();
        else this.syncVideos();
      }, { signal: this.signal });
      this.reducedMotion.addEventListener?.('change', () => this.syncVideos(), { signal: this.signal });
      this.syncVideos();
    }

    activeVideo() {
      if (this.root.dataset.exitState === 'EXIT_TO_HOME' || this.reducedMotion.matches || this.saveData || document.hidden) return null;
      const activePath = this.root.dataset.activePath;
      if (activePath) return this.root.querySelector(`[data-ldw-media="${activePath}"] video`);
      return this.root.querySelector('[data-ldw-media="base"] video');
    }

    syncVideos() {
      const active = this.activeVideo();
      this.videos.forEach((video) => {
        if (video === active) video.play()?.catch?.(() => {});
        else video.pause();
      });
    }

    pauseAll() {
      this.videos.forEach((video) => video.pause());
    }

    destroy() {
      this.abort.abort();
      this.pauseAll();
      document.body.classList.remove('ldw-page-active', 'ldw-gate-active', 'ldw-hide-footer');
      delete this.root.dataset.ldWelcomeVisualReady;
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-ld-welcome]').forEach((root) => {
      if (instances.has(root)) return;
      const instance = new WelcomeVisualController(root);
      instances.set(root, instance);
      instance.connect();
    });
  };
  const unload = (scope) => {
    scope.querySelectorAll?.('[data-ld-welcome]').forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
